import { type MutationState, useMutationState } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';
import { queryClient } from '../../lib';
import type { GetWorkspaceNameCheckResponse } from '../../lib/api/workspaces';
import { useLazyGetWorkspaceNameValidationQuery } from '../api/useWorkspaces';

export const useWorkspaceNameValidation = () => {
  const {
    mutate: runGetWorkspaceNameValidation,
    mutateAsync: validateWorkspaceName,
    ...getWorkspaceNameValidationResponse
  } = useLazyGetWorkspaceNameValidationQuery();

  const [params, setParams] = useState({
    workspaceName: '',
    workspaceID: 0,
    workspaceTypeId: undefined as number | undefined,
  });

  const { isLoading, data } = getWorkspaceNameValidationResponse;

  const workspaceNameValidationMutations = useMutationState({
    filters: { mutationKey: ['getWorkspaceNameValidation'] },
    select: (m) => m.state as MutationState<GetWorkspaceNameCheckResponse>,
  });

  const { isWorkspaceNameValid, isWorkspaceNameValidating } = useMemo(() => {
    const lastMutation = workspaceNameValidationMutations[workspaceNameValidationMutations.length - 1];

    return {
      isWorkspaceNameValid: lastMutation?.data?.workspaceNameAvailable ?? true,
      isWorkspaceNameValidating: lastMutation?.status === 'pending',
    };
  }, [workspaceNameValidationMutations]);

  const resetWorkspaceNameValidation = () => {
    const allMutations = queryClient.getMutationCache().findAll({ mutationKey: ['getWorkspaceNameValidation'] });
    allMutations.forEach((mutation) => {
      queryClient.getMutationCache().remove(mutation);
    });
  };

  const debouncedValidation = useMemo(
    () =>
      debounce((workspaceName: string, workspaceID: number, workspaceTypeId?: number) => {
        setParams({
          workspaceName,
          workspaceID,
          workspaceTypeId,
        });
      }, 350),
    [],
  );

  useEffect(() => {
    if (!params.workspaceID || !params.workspaceName) {
      return;
    }

    runGetWorkspaceNameValidation(params);
  }, [params, runGetWorkspaceNameValidation]);

  return {
    getWorkspaceNameValidation: debouncedValidation,
    isNameValidating: isLoading,
    workspaceNameValidationData: data,
    isWorkspaceNameValid,
    isWorkspaceNameValidating,
    resetWorkspaceNameValidation,
    validateWorkspaceName,
  };
};
